import React, { useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";
import { FaStar, FaCheckCircle, FaWrench, FaCar, FaLeaf, FaCrown } from "react-icons/fa";

/** Deterministic pseudo-random values from id (stable between renders) */
function seedFromId(id) {
  const s = String(id ?? "0");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * Card tiers — top accent color + Book button gradient (rotates by card index).
 */
const TIERS = [
  {
    key: "sponsored",
    topAccent: "bg-amber-500",
    HeaderIcon: FaWrench,
    bookClass:
      "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 shadow-md shadow-amber-900/35",
  },
  {
    key: "featured",
    topAccent: "bg-blue-500",
    HeaderIcon: FaCar,
    bookClass:
      "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 shadow-md shadow-blue-900/35",
  },
  {
    key: "partner",
    topAccent: "bg-emerald-500",
    HeaderIcon: FaLeaf,
    bookClass:
      "bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 shadow-md shadow-emerald-900/35",
  },
  {
    key: "premium",
    topAccent: "bg-violet-500",
    HeaderIcon: FaCrown,
    bookClass:
      "bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 shadow-md shadow-violet-900/35",
  },
];

/** Service tags — order & border tints as in reference (Brakes, Clutch, Engine, AC). */
const SERVICE_TAGS = [
  { label: "Brakes", className: "border-pink-400/55 text-pink-100/95 bg-pink-950/30" },
  { label: "Clutch", className: "border-purple-400/55 text-purple-100/95 bg-purple-950/30" },
  { label: "Engine", className: "border-blue-400/55 text-blue-100/95 bg-blue-950/30" },
  { label: "AC", className: "border-cyan-400/55 text-cyan-100/95 bg-cyan-950/30" },
];

const SERVICE_TAGS_LIGHT = [
  { label: "Brakes", className: "border-pink-200 text-pink-900 bg-pink-50" },
  { label: "Clutch", className: "border-purple-200 text-purple-900 bg-purple-50" },
  { label: "Engine", className: "border-blue-200 text-blue-900 bg-blue-50" },
  { label: "AC", className: "border-cyan-200 text-cyan-900 bg-cyan-50" },
];

/**
 * Garage card — top accent bar, thumb | icon row,
 * name + Open/Closed, location, stars + verified, four service tags, stats row, footer.
 * @param {boolean} compact — denser type/spacing for 8-up grid (layout unchanged).
 */
function NearbyGarageCard({ workshop, tierIndex = 0, index = 0, compact = false }) {
  const { isDark } = useTheme();
  const tier = TIERS[tierIndex % TIERS.length];
  const HeaderIcon = tier.HeaderIcon;
  const seedKey =
    workshop._showcaseSlot != null ? `${workshop.id}_${workshop._showcaseSlot}` : workshop.id;
  const seed = seedFromId(seedKey);
  const maxTilt = compact ? 9 : 13;
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const handlePointerMove = useCallback(
    (e) => {
      const el = cardRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / Math.max(r.width, 1);
      const py = (e.clientY - r.top) / Math.max(r.height, 1);
      const ry = (px - 0.5) * 2 * maxTilt;
      const rx = -(py - 0.5) * 2 * maxTilt;
      setTilt({ rx, ry });
    },
    [maxTilt]
  );

  const handlePointerLeave = useCallback(() => {
    setTilt({ rx: 0, ry: 0 });
  }, []);

  const stats = workshop.stats || {};

  const rating = useMemo(() => {
    if (workshop.rating != null && Number.isFinite(Number(workshop.rating))) {
      return Math.min(5, Math.max(0, Number(workshop.rating)));
    }
    const base = 4.2 + (seed % 8) / 10;
    return Math.min(5, Math.round(base * 10) / 10);
  }, [seed, workshop.rating]);

  const reviews = stats.reviewCount != null
    ? Number(stats.reviewCount)
    : 120 + (seed % 900);
  const tierLabel = workshop.tier || workshop.garageTier || null;
  const verificationLabel = workshop.clusterVerificationStatus === "verified"
    ? "Verified"
    : workshop.clusterVerificationStatus === "self_declared"
      ? "Self-declared"
      : "Verified";
  const fulfillmentFlags = workshop.fulfillmentFlags || {};
  const fulfillmentItems = [
    fulfillmentFlags.pickupDrop && { key: "pickup", label: "Pickup" },
    fulfillmentFlags.doorstep && { key: "doorstep", label: "Doorstep" },
    fulfillmentFlags.roadsideAssistance && { key: "rsa", label: "RSA" },
    fulfillmentFlags.sameDayTurnaround && { key: "sameDay", label: "Same day" },
  ].filter(Boolean);
  const years =
    stats.platformYears != null && Number.isFinite(Number(stats.platformYears))
      ? Number(stats.platformYears)
      : 5 + (seed % 8);
  const jobsValue =
    stats.jobsValue != null && String(stats.jobsValue).length > 0
      ? String(stats.jobsValue)
      : (1.2 + (seed % 50) / 10).toFixed(1);
  const jobsSuffix =
    stats.jobsSuffix != null ? String(stats.jobsSuffix) : "K";
  const satisfied =
    stats.satisfactionPct != null && Number.isFinite(Number(stats.satisfactionPct))
      ? Number(stats.satisfactionPct)
      : 94 + (seed % 6);
  const dealPrice = Number(workshop.bestDeal?.dealPrice);
  const price =
    dealPrice > 0
      ? Math.round(dealPrice)
      : 249 + (seed % 5) * 50;
  const isOpen =
    typeof stats.isOpen === "boolean" ? stats.isOpen : seed % 9 !== 0;

  const distanceText =
    typeof workshop.distance === "number" && Number.isFinite(workshop.distance)
      ? `${workshop.distance.toFixed(1)} km`
      : "—";

  const locationLine = workshop.location || "Location on map";
  const thumbLetter = (workshop.name || "?").trim().charAt(0).toUpperCase() || "?";

  const tiltActive = Math.abs(tilt.rx) > 0.05 || Math.abs(tilt.ry) > 0.05;

  const rnd = compact ? "rounded-xl" : "rounded-2xl";
  const pad = compact ? "px-3 pt-2.5 pb-3" : "px-3 pt-3 pb-3";
  const padHeader = compact ? "px-3 pt-2.5 pb-2 gap-2" : "px-3 pt-3 pb-2 gap-2";
  const thumbSz = compact ? "w-9 h-9 text-xs" : "w-10 h-10 text-sm";
  const iconWrap = compact ? "w-12 h-12 rounded-lg" : "w-14 h-14 rounded-xl";
  const iconInner = compact ? "text-xl" : "text-2xl";
  const titleCls = compact ? "text-sm sm:text-[15px]" : "text-sm";
  const statusCls = compact ? "text-[10px]" : "text-[11px]";
  const locCls = compact ? "text-[11px] mb-2" : "text-xs mb-2.5";
  const starSz = compact ? "text-[10px]" : "text-sm";
  const ratingTxt = compact ? "text-[11px]" : "text-xs";
  const verifiedCls = compact ? "text-[9px] px-1.5 py-0.5 gap-0.5" : "text-[10px] px-2 py-0.5 gap-1";
  const tagCls = compact ? "text-[9px] px-2 py-0.5 gap-0.5" : "text-[10px] px-2 py-0.5 gap-1";
  const statNum = compact ? "text-xs" : "text-sm";
  const statLbl = compact ? "text-[8px] mt-0.5" : "text-[9px] mt-0.5";
  const statPad = compact ? "py-2 mb-2" : "py-3 mb-3";
  const footFrom = compact ? "text-[11px]" : "text-xs";
  const btnBook = compact ? "rounded-md px-2.5 py-1.5 text-[11px]" : "rounded-lg px-3.5 py-2 text-xs";
  const btnView = compact ? "text-[11px]" : "text-xs";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{
        duration: 0.45,
        delay: Math.min(index * 0.05, 0.28),
        ease: [0.22, 1, 0.36, 1],
      }}
      className="h-full [perspective:1100px] [perspective-origin:center]"
    >
      <motion.div
        ref={cardRef}
        className={[
          `relative h-full flex flex-col overflow-hidden group/card will-change-transform ${rnd}`,
          isDark
            ? "border border-slate-700/80 bg-[#1A1D23]"
            : "border border-gray-200 bg-white",
          compact
            ? isDark
              ? "shadow-[0_8px_22px_-6px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.04]"
              : "shadow-md ring-1 ring-black/[0.04]"
            : isDark
              ? "shadow-[0_16px_40px_-12px_rgba(0,0,0,0.65)] ring-1 ring-white/[0.05]"
              : "shadow-lg ring-1 ring-black/[0.05]",
          "transform-gpu [transform-style:preserve-3d]",
        ].join(" ")}
        style={{ transformStyle: "preserve-3d" }}
        animate={{
          rotateX: tilt.rx,
          rotateY: tilt.ry,
          z: tiltActive ? (compact ? 10 : 14) : 0,
        }}
        transition={{
          type: "spring",
          stiffness: 420,
          damping: 34,
          mass: 0.55,
        }}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      >
        {(workshop.bestDeal?.fullyBooked ||
          workshop.bestDeal?.type === "fully_booked" ||
          workshop.bestDeal?.label ||
          (workshop.bestDeal?.type === "percent" && Number(workshop.bestDeal?.value) > 0)) && (
          <div
            className={`absolute z-20 font-extrabold tracking-wide shadow-lg pointer-events-none ${
              compact ? "top-2 right-2 text-[9px] px-2 py-0.5 rounded-md" : "top-3 right-3 text-[11px] px-2.5 py-1 rounded-lg"
            } ${
              workshop.bestDeal?.fullyBooked || workshop.bestDeal?.type === "fully_booked"
                ? isDark
                  ? "bg-slate-700 text-amber-200 border border-amber-500/60"
                  : "bg-slate-800 text-amber-100 border border-amber-400"
                : isDark
                  ? "bg-gradient-to-r from-rose-600 to-orange-500 text-white border border-rose-400/50"
                  : "bg-gradient-to-r from-rose-500 to-orange-500 text-white border border-rose-200"
            }`}
            aria-label={
              workshop.bestDeal?.fullyBooked || workshop.bestDeal?.type === "fully_booked"
                ? "Fully booked"
                : "Service deal discount"
            }
          >
            {workshop.bestDeal?.fullyBooked || workshop.bestDeal?.type === "fully_booked"
              ? "Fully Booked"
              : workshop.bestDeal?.label ||
                `${Math.round(Number(workshop.bestDeal?.value) || 0)}% OFF`}
          </div>
        )}

        {/* Colored top accent (tier) */}
        <div className={`h-1 w-full shrink-0 ${tier.topAccent}`} aria-hidden />

        {/* Header row: thumbnail | center icon */}
        <div
          className={`flex items-center shrink-0 ${isDark ? "bg-[#1A1D23]" : "bg-slate-50"} ${padHeader}`}
        >
          <div
            className={`${thumbSz} shrink-0 rounded-lg flex items-center justify-center font-bold ${
              isDark
                ? "bg-slate-800/90 border border-slate-600/90 text-slate-200"
                : "bg-white border border-gray-200 text-slate-800"
            }`}
          >
            {thumbLetter}
          </div>
          <div className="flex-1 flex justify-center min-w-0">
            <div
              className={`${iconWrap} flex items-center justify-center ${
                isDark ? "bg-slate-800/70 border border-slate-600/70" : "bg-white border border-gray-200"
              }`}
            >
              <HeaderIcon className={`${iconInner} ${isDark ? "text-white/95" : "text-slate-700"}`} />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className={`flex-1 flex flex-col ${isDark ? "bg-[#1A1D23]" : "bg-white"} ${pad}`}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="min-w-0 flex-1">
              <h3
                className={`font-bold leading-snug line-clamp-2 ${titleCls} ${
                  isDark ? "text-white" : "text-slate-900"
                }`}
              >
                {workshop.name}
              </h3>
              {tierLabel && (
                <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  isDark ? "bg-slate-800 text-slate-300 border border-slate-600" : "bg-slate-100 text-slate-700 border border-slate-200"
                }`}>
                  {tierLabel} garage
                </span>
              )}
            </div>
            {isOpen ? (
              <span className={`shrink-0 inline-flex items-center gap-1 font-semibold text-emerald-400 whitespace-nowrap ${statusCls}`}>
                <span className={`rounded-full bg-emerald-400 ${compact ? "w-1 h-1" : "w-1.5 h-1.5"}`} />
                Open
              </span>
            ) : (
              <span className={`shrink-0 inline-flex items-center gap-1 font-semibold text-red-400 whitespace-nowrap ${statusCls}`}>
                <span className={`rounded-full bg-red-400 ${compact ? "w-1 h-1" : "w-1.5 h-1.5"}`} />
                Closed
              </span>
            )}
          </div>

          {workshop.matchReason && (
            <p className={`${compact ? "text-[10px] mb-1.5" : "text-xs mb-2"} font-medium ${isDark ? "text-blue-300" : "text-blue-700"}`}>
              {workshop.matchReason}
            </p>
          )}

          <p className={`flex items-start gap-1 line-clamp-2 ${locCls} ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            <span className="shrink-0" aria-hidden>
              📍
            </span>
            <span>
              {locationLine}
              {distanceText !== "—" && (
                <span className={isDark ? "text-slate-500" : "text-slate-500"}> · {distanceText}</span>
              )}
            </span>
          </p>

          <div className={`flex flex-wrap items-center ${compact ? "gap-1 mb-1.5" : "gap-2 mb-2.5"}`}>
            <div className="flex items-center gap-px">
              {[1, 2, 3, 4, 5].map((i) => (
                <FaStar
                  key={i}
                  className={
                    i <= Math.round(rating)
                      ? `text-amber-400 drop-shadow-sm ${starSz}`
                      : `${isDark ? "text-slate-600" : "text-gray-300"} ${starSz}`
                  }
                />
              ))}
            </div>
            <span className={`font-semibold ${isDark ? "text-slate-200" : "text-slate-800"} ${ratingTxt}`}>
              {Number(rating).toFixed(1)} ({reviews})
            </span>
            <span
              className={`inline-flex items-center rounded-full font-semibold ${verifiedCls} ${
                isDark
                  ? "bg-emerald-950/55 border border-emerald-700/50 text-emerald-300"
                  : "bg-emerald-50 border border-emerald-200 text-emerald-800"
              }`}
            >
              <FaCheckCircle className={compact ? "text-[10px]" : "text-[11px] text-emerald-400"} />
              {verificationLabel}
            </span>
          </div>

          {fulfillmentItems.length > 0 && (
            <div className={`flex flex-wrap ${compact ? "gap-1 mb-1.5" : "gap-1.5 mb-2"}`}>
              {fulfillmentItems.map((item) => (
                <span
                  key={item.key}
                  className={`inline-flex items-center rounded-full border font-medium ${tagCls} ${
                    isDark
                      ? "border-indigo-500/50 text-indigo-200 bg-indigo-950/40"
                      : "border-indigo-200 text-indigo-800 bg-indigo-50"
                  }`}
                >
                  {item.label}
                </span>
              ))}
            </div>
          )}

          <div className={`flex flex-wrap ${compact ? "gap-1 mb-2" : "gap-1.5 mb-3"}`}>
            {(Array.isArray(workshop.services) && workshop.services.length > 0
              ? workshop.services.slice(0, 4).map((label) => ({
                  label,
                  className: isDark
                    ? "border-slate-500/55 text-slate-200/95 bg-slate-800/40"
                    : "border-slate-200 text-slate-800 bg-slate-50"
                }))
              : isDark
                ? SERVICE_TAGS
                : SERVICE_TAGS_LIGHT
            ).map((s) => (
              <span
                key={s.label}
                className={`inline-flex items-center rounded-full border font-medium ${tagCls} ${s.className}`}
              >
                {s.label}
              </span>
            ))}
          </div>

          <div
            className={`grid grid-cols-3 gap-0.5 border-y ${statPad} ${
              isDark ? "border-slate-700/90" : "border-gray-200"
            }`}
          >
            <div className={`text-center pr-1 ${isDark ? "border-r border-slate-700/80" : "border-r border-gray-200"}`}>
              <p className={`font-bold ${isDark ? "text-white" : "text-slate-900"} ${statNum}`}>{years} yr</p>
              <p className={`uppercase tracking-wider font-semibold ${statLbl} ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                Platform
              </p>
            </div>
            <div className={`text-center px-1 ${isDark ? "border-r border-slate-700/80" : "border-r border-gray-200"}`}>
              <p className={`font-bold ${isDark ? "text-white" : "text-slate-900"} ${statNum}`}>
                {jobsValue}
                {jobsSuffix}
              </p>
              <p className={`uppercase tracking-wider font-semibold ${statLbl} ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                Jobs
              </p>
            </div>
            <div className="text-center pl-1">
              <p className={`font-bold ${isDark ? "text-white" : "text-slate-900"} ${statNum}`}>{satisfied}%</p>
              <p className={`uppercase tracking-wider font-semibold ${statLbl} ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                Satisfied
              </p>
            </div>
          </div>

          <div className={`mt-auto flex items-center justify-between ${compact ? "gap-1" : "gap-2"}`}>
            <span className={`font-medium ${isDark ? "text-amber-400" : "text-amber-700"} ${footFrom}`}>
              From <span className="font-bold">₹{price}</span>
            </span>
            <div className={`flex items-center ${compact ? "gap-1" : "gap-2"}`}>
              <Link
                to={{
                  pathname: `/service/garage/${workshop.id}`,
                  state: { distance: workshop.distance },
                }}
                className={`font-semibold transition-colors ${btnView} ${
                  isDark ? "text-slate-400 hover:text-white" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                View
              </Link>
              <Link
                to={{
                  pathname: `/service/garage/${workshop.id}`,
                  state: { distance: workshop.distance },
                }}
                className={[
                  "sp-btn-cta inline-flex items-center justify-center font-bold text-white",
                  btnBook,
                  "transition-transform duration-200 hover:scale-[1.03] active:scale-[0.98]",
                  tier.bookClass,
                ].join(" ")}
              >
                Book Now
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default NearbyGarageCard;
